import type { SignatureDetails } from "@/shared/types";
import { renderTemplate } from "@/shared/services/template-renderer";
import { normalizeUserData } from "@/shared/services/user-normalizer";
import { info, debug, warn } from "@/shared/services/logger";

const SOURCE = "sig-renderer";

/**
 * Extrai a primeira imagem base64 de um HTML para uso como anexo inline.
 *
 * @param html - String HTML contendo tags `<img>`.
 * @returns Dados base64 e nome do arquivo, ou `null` se nenhuma imagem for encontrada.
 *
 * @example
 * ```typescript
 * const logo = extractLogoForInline('<img src="data:image/png;base64,ABC123">');
 * // { base64: "data:image/png;base64,ABC123", fileName: "logo.png" }
 * ```
 */
export function extractLogoForInline(html: string): { base64: string; fileName: string } | null {
  const match = html.match(/<img[^>]+src="(data:image\/[^"]+)"[^>]*>/i);
  if (match) {
    const base64 = match[1];
    const extMatch = base64.match(/^data:image\/(\w+);/);
    const ext = extMatch ? extMatch[1] : "png";
    debug(SOURCE, `extractLogoForInline: logo encontrado — formato=${ext}, tamanho=${base64.length} chars.`);
    return { base64, fileName: `logo.${ext}` };
  }
  debug(SOURCE, "extractLogoForInline: nenhum logo inline encontrado no template.");
  return null;
}

/**
 * Renderiza o HTML da assinatura a partir do template e extrai logotipo inline.
 *
 * @param templateHtml - O HTML do template.
 * @param userData - Dados do usuário para substituição.
 * @returns Detalhes da assinatura com HTML renderizado e logotipo opcional.
 *
 * @example
 * ```typescript
 * const details = renderSignature(tpl.html, { name: "João", email: "j@e.com" });
 * ```
 */
export function renderSignature(
  templateHtml: string,
  userData: Record<string, any>
): SignatureDetails {
  debug(SOURCE, `renderSignature: template=${templateHtml.length} chars, userFields=${Object.keys(userData).length}`);
  const hasPlaceholder1 = templateHtml.includes("office_phone_1");
  const hasPlaceholder2 = templateHtml.includes("office_phone_2");
  debug(SOURCE, `[TEL-DEBUG] renderSignature: template contém placeholder office_phone_1=${hasPlaceholder1}, office_phone_2=${hasPlaceholder2}.`);

  const normalized = normalizeUserData(userData);
  const rendered = renderTemplate(templateHtml, normalized);
  const logoInfo = extractLogoForInline(templateHtml);

  if (normalized.office_phone_1 && !rendered.includes(normalized.office_phone_1)) {
    warn(SOURCE, `[TEL-DEBUG] office_phone_1 (${normalized.office_phone_1}) NÃO apareceu no HTML renderizado — template sem placeholder ou bloco condicional removeu o trecho.`);
  }
  if (normalized.office_phone_2 && !rendered.includes(normalized.office_phone_2)) {
    warn(SOURCE, `[TEL-DEBUG] office_phone_2 (${normalized.office_phone_2}) NÃO apareceu no HTML renderizado — template sem placeholder ou bloco condicional removeu o trecho.`);
  }

  info(SOURCE, `renderSignature concluído: output=${rendered.length} chars, logo=${logoInfo ? "sim" : "não"}`);
  return {
    signature: rendered,
    logoBase64: logoInfo?.base64 || null,
    logoFileName: logoInfo?.fileName || null,
  };
}
